import { useEffect, useState } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  History, Plus, Loader2, Brain, FileText,
} from 'lucide-react';
import { aiActApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatDateTime, cn } from '../../lib/utils';

const EVENT_TYPES = [
  { value: 'decision', label: 'Decisão', color: 'bg-blue-50 text-blue-700' },
  { value: 'input_received', label: 'Input recebido', color: 'bg-slate-50 text-slate-700' },
  { value: 'output_generated', label: 'Output gerado', color: 'bg-purple-50 text-purple-700' },
  { value: 'human_override', label: 'Override humano', color: 'bg-amber-50 text-amber-700' },
  { value: 'model_update', label: 'Update modelo', color: 'bg-indigo-50 text-indigo-700' },
  { value: 'error', label: 'Erro', color: 'bg-red-50 text-red-700' },
  { value: 'anomaly', label: 'Anomalia', color: 'bg-orange-50 text-orange-700' },
  { value: 'conformity_check', label: 'Verif. conformidade', color: 'bg-green-50 text-green-700' },
];

const initLog = { system_id: '', event_type: 'decision', event_description: '', output_summary: '', human_override: false, override_reason: '', retention_days: 180 };

export default function AiAuditTrail() {
  const [logs, setLogs] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemFilter, setSystemFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initLog);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [systemFilter, typeFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const p = { limit: 200 };
      if (systemFilter) p.system_id = systemFilter;
      if (typeFilter) p.event_type = typeFilter;
      const [l, s] = await Promise.all([aiActApi.listAudit(p), aiActApi.listSystems({})]);
      setLogs(l.data); setSystems(s.data);
    } catch { toast.error('Erro ao carregar audit trail'); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.system_id) { toast.error('Sistema obrigatório'); return; }
    setSaving(true);
    try {
      await aiActApi.createAudit({
        ...form,
        retention_days: Number(form.retention_days) || 180,
      });
      toast.success('Evento registado');
      setShowForm(false); setForm(initLog); load();
    } catch { toast.error('Erro ao registar'); }
    finally { setSaving(false); }
  };

  const getSysName = (sid) => systems.find(s => s.system_id === sid)?.name || sid;
  const typeCfg = (t) => EVENT_TYPES.find(x => x.value === t) || { label: t, color: 'bg-slate-50 text-slate-700' };

  if (loading) return <Layout title="EU AI Act — Audit Trail"><div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin" /></div></Layout>;

  return (
    <Layout title="EU AI Act — Audit Trail (F6 — Art. 12)" headerContent={
      <Button onClick={() => setShowForm(true)} data-testid="new-log-btn">
        <Plus className="h-4 w-4 mr-2" />Registar Evento
      </Button>
    }>
      <div className="space-y-6" data-testid="audit-trail-container">

        <Card className="card-flat bg-indigo-50 border-indigo-200">
          <CardContent className="p-4 flex items-start gap-3">
            <FileText className="h-5 w-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-medium text-indigo-900">Logging Automático (Art. 12)</p>
              <p className="text-sm text-indigo-700 mt-1">
                Sistemas IA de alto risco devem registar automaticamente eventos durante o seu funcionamento.
                Retenção mínima: 6 meses (180 dias). Aqui pode registar eventos manuais (overrides, anomalias detetadas, etc.).
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="card-flat">
          <CardContent className="p-4 flex gap-3 flex-wrap">
            <Select value={systemFilter || 'all'} onValueChange={v => setSystemFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[250px]" data-testid="filter-audit-system"><SelectValue placeholder="Todos os sistemas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os sistemas</SelectItem>
                {systems.map(s => <SelectItem key={s.system_id} value={s.system_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={typeFilter || 'all'} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px]" data-testid="filter-audit-type"><SelectValue placeholder="Tipo de evento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {logs.length === 0 ? (
          <Card className="card-flat"><CardContent className="p-8 text-center">
            <History className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Sem eventos registados</p>
          </CardContent></Card>
        ) : (
          <Card className="card-flat" data-testid="audit-list">
            <CardContent className="p-0">
              <div className="divide-y">
                {logs.map(log => {
                  const tCfg = typeCfg(log.event_type);
                  return (
                    <div key={log.audit_id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`log-${log.audit_id}`}>
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="font-mono text-xs text-slate-400">{log.audit_id}</span>
                        <Badge variant="outline" className={cn('text-xs', tCfg.color)}>{tCfg.label}</Badge>
                        {log.human_override && (
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Override Humano</Badge>
                        )}
                        <Badge variant="outline" className="text-xs">{getSysName(log.system_id)}</Badge>
                        <span className="text-xs text-slate-500 ml-auto">{formatDateTime(log.logged_at)}</span>
                      </div>
                      {log.event_description && <p className="text-sm text-slate-800">{log.event_description}</p>}
                      {log.output_summary && <p className="text-xs text-slate-600 mt-1"><strong>Output:</strong> {log.output_summary}</p>}
                      {log.override_reason && <p className="text-xs text-amber-700 mt-1"><strong>Motivo override:</strong> {log.override_reason}</p>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Registar Evento de Auditoria</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Sistema IA *</Label>
              <Select value={form.system_id} onValueChange={v => setForm({ ...form, system_id: v })}>
                <SelectTrigger data-testid="log-system"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{systems.map(s => <SelectItem key={s.system_id} value={s.system_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de evento</Label>
              <Select value={form.event_type} onValueChange={v => setForm({ ...form, event_type: v })}>
                <SelectTrigger data-testid="log-type"><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea rows={3} value={form.event_description} onChange={e => setForm({ ...form, event_description: e.target.value })} data-testid="log-desc" />
            </div>
            <div className="space-y-2">
              <Label>Resumo do output</Label>
              <Textarea rows={2} value={form.output_summary} onChange={e => setForm({ ...form, output_summary: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={form.human_override} onCheckedChange={c => setForm({ ...form, human_override: !!c })} id="ho" data-testid="log-override" />
              <Label htmlFor="ho">Foi feito override humano da decisão</Label>
            </div>
            {form.human_override && (
              <div className="space-y-2">
                <Label>Motivo do override</Label>
                <Textarea rows={2} value={form.override_reason} onChange={e => setForm({ ...form, override_reason: e.target.value })} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Retenção (dias) — mínimo 180 (Art. 12)</Label>
              <Input type="number" min="180" value={form.retention_days} onChange={e => setForm({ ...form, retention_days: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-log-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Registar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
